import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, WritableSignal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ApolloLink } from '@apollo/client/core';
import { environment } from '@env/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { SelectOption } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import cloneDeep from 'lodash-es/cloneDeep';
import { Observable, map, of, switchMap } from 'rxjs';
import { SupplierMappingTypeDetails } from 'settings/interfaces/mapping-type-details.interface';
import {
    AiUsageType,
    getAiUsageTypeFromCode,
    getAiUsageTypeRoute,
    SetSupplierSpendingLimitRequest,
    SupplierSpendingLimit,
    SupplierSpendingLimitResponse,
} from 'settings/interfaces/supplier-spending-limit.interface';
import { SupplierTokenLimit, SupplierTokenLimitResponse } from 'settings/interfaces/supplier-token-limit.interface';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { BypassErrorLink } from 'shared/abstract/bypass-error-link';
import { ID_ZERO } from 'shared/constants/general-constants';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService, GlobalConfigService } from 'shared/services';

const supplierManagementURL = environment.BASE_URL + 'supplierManagementApi/';
const uploadURL = environment.BASE_URL + 'supplierManagementApi/upload';
const deleteURL = environment.BASE_URL + 'supplierManagementApi/deleteFiles';

@Injectable({
    providedIn: 'root',
})
export class SuppliersService implements GridService {
    private apollo: ApolloBase;

    public supplierToEdit: WritableSignal<Supplier | null> = signal(null);
    public currentSupplierByBrand = signal<Supplier | null>(null);

    loadAllSuppliers = signal<boolean>(false);
    loadAllSuppliers$ = toObservable(this.loadAllSuppliers);

    allSuppliersList = toSignal(
        this.loadAllSuppliers$.pipe(
            switchMap((loadAllSuppliers) => {
                if (!loadAllSuppliers) {
                    return of([] as any[]);
                }
                return this.getAllSuppliers(false);
            }),
        ),
        { initialValue: [] as any[] },
    );

    supplierIdForBrandCodes = signal<string | null>(null);
    supplierIdForBrandCodes$ = toObservable(this.supplierIdForBrandCodes);

    brandListBySupplier = toSignal(
        this.supplierIdForBrandCodes$.pipe(
            switchMap((supplierId) => {
                if (supplierId === '' || supplierId === null) {
                    return of([] as SelectOption[]);
                }
                return this.http.get<SelectOption[]>(environment.BASE_URL + 'supplierManagementApi/brandCodes', {
                    params: { supplier: supplierId },
                });
            }),
        ),
        { initialValue: [] as SelectOption[] },
    );

    constructor(
        private apolloProvider: Apollo,
        private http: HttpClient,
        private cachingService: CachingService,
        private globalConfigService: GlobalConfigService,
    ) {
        this.apollo = this.apolloProvider.use('suppliersAPI');
    }

    NAME = 'SuppliersGridService';

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const filterModel = params.request.filterModel as FilterModel;

        return this.apollo
            .query({
                query: gql`
                query getSuppliers ($orderBy: String,
                    $orderDirection: OrderDirection,
                    $companyName: String,
                    $companyShortName: String,
                    $classIds: [Guid],
                    $createdAt: DateOnly
                ){
                    suppliers {
                        searchSuppliers(
                            searchQuery: {
                                pageNumber: ${pageNumber},
                                pageSize: ${pageSize},
                                orderBy: $orderBy,
                                orderDirection: $orderDirection
                            },
                            filtering: {
                                companyName: $companyName,
                                companyShortName: $companyShortName,
                                classIds: $classIds,
                                createdAt: $createdAt,
                            }
                        ) {
                            data {
                                id,
                                companyName,
                                companyShortName,
                                contactName,
                                classId,
                                className,
                                enabled,
                                supplierAiSettings {
                                    autoShorteningEnabled
                                    autoTranslationEnabled
                                    imageEditingEnabled
                                },
                                supplierSpendingLimits {
                                    usage
                                    limitAmount
                                    isUnlimited
                                    markupPercentage
                                    isActive
                                },
                                entityTracking {
                                    createdBy,
                                    createdAt,
                                    modifiedBy,
                                    modifiedAt
                                }
                            }
                            totalCount
                        }
                    }
                }
            `,
                variables: {
                    orderBy: params.request.sortModel[0]?.colId ?? 'companyName',
                    orderDirection: params.request.sortModel[0]?.sort.toUpperCase() ?? 'ASC',
                    companyName: filterModel['companyName']?.filter,
                    companyShortName: filterModel['companyShortName']?.filter,
                    classIds: this.globalConfigService.getSupplierClassIdsFromNames(filterModel['classId']?.filter),
                    createdAt: filterModel['createdAt']?.filter
                        ? formatDate(filterModel['createdAt']?.filter, 'yyyy-MM-dd', 'en-US')
                        : undefined,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const classSort = params.request.sortModel[0]?.colId === 'classId';
                    let sortedResponse = cloneDeep(response.data.suppliers.searchSuppliers);
                    const positiveSortDirection = params.request.sortModel[0]?.sort.toUpperCase() === 'ASC' ? 1 : 0;

                    if (classSort) {
                        const cloneResponse = cloneDeep(response.data.suppliers.searchSuppliers.data);
                        cloneResponse.sort((obj1: Supplier, obj2: Supplier) => {
                            const class1 = this.globalConfigService.getSupplierClassNameFromId(obj1.className);
                            const class2 = this.globalConfigService.getSupplierClassNameFromId(obj2.className);

                            if (class1 === null) return 1;
                            if (class2 === null) return -1;

                            if (class1 > class2) {
                                return positiveSortDirection ? 1 : -1;
                            }

                            if (class1 < class2) {
                                return positiveSortDirection ? -1 : 1;
                            }

                            return 0;
                        });

                        sortedResponse = {
                            ...response.data.suppliers.searchSuppliers,
                            data: cloneResponse,
                        };
                    }

                    return sortedResponse;
                }),
            );
    }

    public getAllSuppliers(
        includePropertyExportsToIcePortal: boolean = false,
        includeSupplierAiFeatures: boolean = false,
    ): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    suppliers {
                        searchSuppliers(searchQuery:
                        {pageNumber: 1,
                            pageSize: 1000,
                        }) {
                            data {
                                id,
                                companyName,
                                hasVisualsUIEnabled,
                                ${includePropertyExportsToIcePortal ? 'exportsToIcePortal' : ''},
                                ${includeSupplierAiFeatures ? 'supplierAiSettings { autoShorteningEnabled, autoTranslationEnabled, imageEditingEnabled },' : ''}
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.suppliers.searchSuppliers.data;
                }),
            );
    }

    public getSupplierPublicData(supplierId: string, inviteContextToken?: string): Observable<Supplier> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    suppliers {
                        searchBaseSuppliers(supplierId: "${supplierId}") {
                            data {
                                id
                                companyName
                                companyShortName
                                logo
                                termsAndConditions
                            }
                            statusCode
                            totalCount
                        }
                    }
                }
            `,
                context: inviteContextToken
                    ? {
                          headers: {
                              Authorization: 'Bearer ' + inviteContextToken,
                              bypassTokenRefreshInterceptor: 'true',
                          },
                      }
                    : undefined,
            })
            .pipe(
                map((response: any) => {
                    return response.data.suppliers.searchBaseSuppliers.data[0];
                }),
            );
    }

    public getSupplierById(supplierId: string, extraFields?: string[]): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    suppliers {
                        searchSuppliers(filtering: {searchTerm: "${supplierId}"}) {
                            data {
                                id,
                                companyName,
                                companyShortName,
                                classId,
                                className,
                                contact,
                                enabled,
                                exportsToIcePortal,
                                isIceportalSSO,
                                isDalSSO,
                                hasTwoFAEnabled,
                                usesBookingRpa,
                                idleTime,
                                forceLogoutTime,
                                hasVisualsUIEnabled,
                                supplierLanguages {
                                    id,
                                    languageId
                                },
                                supplierAiSettings {
                                    autoShorteningEnabled
                                    autoTranslationEnabled
                                    imageEditingEnabled
                                },
                                supplierSpendingLimits {
                                    usage
                                    limitAmount
                                    isUnlimited
                                    markupPercentage
                                    isActive
                                },
                                logo,
                                termsAndConditions,
                                sharedIdentifier,
                                entityTracking {
                                    createdBy,
                                    createdAt,
                                    modifiedBy,
                                    modifiedAt
                                }
                                ${extraFields && extraFields.length ? ', ' + extraFields?.join(',') : ''}
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.suppliers.searchSuppliers.data[0];
                }),
            );
    }

    public getSupplierName(supplierId: string) {
        return this.apollo
            .query({
                query: gql`
                query search {
                    suppliers {
                        searchSuppliers(filtering: {searchTerm: "${supplierId}"}) {
                            data {
                                id
                                companyName
                            },
                            statusCode
                            message
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.suppliers.searchSuppliers.data;
                }),
            );
    }

    public getAllSupplierMappings(supplierId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    mappingTypes {
                        searchSupplierMappings(filtering: {supplierId: "${supplierId}"}) {
                            data {
                                id,
                                description,
                                regexValidation,
                                isUnique,
                                isDefault,
                                supplierId,
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.mappingTypes.searchSupplierMappings.data;
                }),
            );
    }

    public updateMultipleSupplierMappingTypes(mappingTypes: SupplierMappingTypeDetails[]): Observable<any> {
        const mutation = `
            mutation updateMultipleSupplierMappings {
                mappingTypes {
                    updateMultipleSupplierMappings (updateMultipleSupplierMappings: [${mappingTypes?.map(
                        (mappingType: SupplierMappingTypeDetails) => `{
                            supplierId: "${mappingType.supplierId}",
                            description: "${mappingType.description}",
                            id: "${mappingType.id !== String.EMPTY ? mappingType.id : ID_ZERO}",
                            isDefault: ${mappingType.isDefault},
                            isUnique: ${mappingType.isUnique},
                            regexValidation: "${mappingType.regexValidation}"
                        },`,
                    )} ]) {
                            statusCode,
                            message
                        }
                    }
                }
        `;
        return this.apollo
            .mutate({
                mutation: gql(mutation),
            })
            .pipe(
                map((response: any) => {
                    return response.data.mappingTypes.updateSupplierMapping;
                }),
            );
    }

    public createSupplier(supplier: Supplier): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation createSupplier {
                    suppliers {
                        createSupplier(
                            createSupplier: {
                                companyName: "${supplier.companyName}",
                                companyShortName: "${supplier.companyShortName}",
                                supplierLanguagesInput: [${supplier.supplierLanguages.map((language) => `{languageId: "${language.languageId}"}`)}],
                                classId: "${supplier.classId}",
                                exportsToIcePortal: ${supplier.exportsToIcePortal},
                                hasVisualsUIEnabled: ${supplier.hasVisualsUIEnabled},
                                isIceportalSSO: ${supplier.isIceportalSSO},
                                isDalSSO: ${supplier.isDalSSO},
                                hasTwoFAEnabled: ${supplier.hasTwoFAEnabled},
                                idleTime: ${supplier.idleTime},
                                forceLogoutTime: ${supplier.forceLogoutTime},
                                ${supplier.sharedIdentifier ? 'sharedIdentifier: "' + supplier.sharedIdentifier + '"' : ''},
                                ${supplier.usesBookingRpa !== null && supplier.usesBookingRpa !== undefined ? 'usesBookingRpa:' + supplier.usesBookingRpa : ''}
                            }) {
                            data {
                                id
                                companyName
                                companyShortName
                            }
                        }
                    }
                }
            `,
                context: {
                    link: ApolloLink.from([new BypassErrorLink([409])]),
                },
            })
            .pipe(
                map((response: any) => {
                    if (response.extensions?.statusCode) {
                        throw response;
                    }

                    return response.data.suppliers.createSupplier.data[0];
                }),
            );
    }

    public updateSupplier(supplier: Supplier): Observable<any> {
        const supplierMutation = `
                    mutation updateSupplier {
                        suppliers {
                            updateSupplier(
                                updateSupplier: {
                                    id: "${supplier.id}",
                                    ${supplier.companyName ? 'companyName: "' + supplier.companyName + '"' : ''},
                                    ${supplier.companyShortName ? 'companyShortName: "' + supplier.companyShortName + '"' : ''},
                                    ${supplier.supplierLanguages ? 'supplierLanguagesInput: [' + supplier.supplierLanguages.map((language) => `{languageId: "${language.languageId}"}`) + ']' : ''},
                                    ${supplier.classId ? 'classId: "' + supplier.classId + '"' : ''},
                                    ${supplier.enabled !== null && supplier.enabled !== undefined ? 'enabled: ' + supplier.enabled : ''},
                                    ${supplier.contact ? 'contact: "' + supplier.contact + '"' : ''},
                                    ${supplier.logo === null ? 'logo: null' : ''},
                                    ${supplier.termsAndConditions === null ? 'termsAndConditions: null' : ''},
                                    ${supplier.exportsToIcePortal !== null && supplier.exportsToIcePortal !== undefined ? 'exportsToIcePortal:' + supplier.exportsToIcePortal : ''},
                                    ${supplier.hasVisualsUIEnabled !== null && supplier.hasVisualsUIEnabled !== undefined ? 'hasVisualsUIEnabled:' + supplier.hasVisualsUIEnabled : ''},
                                    ${supplier.isIceportalSSO !== null && supplier.isIceportalSSO !== undefined ? 'isIceportalSSO:' + supplier.isIceportalSSO : ''},
                                    ${supplier.isDalSSO !== null && supplier.isDalSSO !== undefined ? 'isDalSSO:' + supplier.isDalSSO : ''},
                                    ${supplier.hasTwoFAEnabled !== null && supplier.hasTwoFAEnabled !== undefined ? 'hasTwoFAEnabled:' + supplier.hasTwoFAEnabled : ''},
                                    ${supplier.idleTime ? 'idleTime:' + supplier.idleTime : ''},
                                    ${supplier.forceLogoutTime ? 'forceLogoutTime:' + supplier.forceLogoutTime : ''},
                                    ${supplier.sharedIdentifier ? 'sharedIdentifier: "' + supplier.sharedIdentifier + '"' : ''},
                                    ${supplier.usesBookingRpa !== null && supplier.usesBookingRpa !== undefined ? 'usesBookingRpa:' + supplier.usesBookingRpa : ''},
                                    ${supplier.supplierAiSettings !== null && supplier.supplierAiSettings !== undefined ? 'supplierAiSettingsInput: ' + JSON.stringify(supplier.supplierAiSettings).replace(/"([^"]+)":/g, '$1:') : ''}
                                }) {
                                statusCode,
                                message
                            }
                        }
                    }
            `;

        return this.apollo
            .mutate({
                mutation: gql(supplierMutation),
            })
            .pipe(
                map((response: any) => {
                    return response;
                }),
            );
    }

    public deleteSupplier(supplier: Supplier): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation deleteSupplier {
                    suppliers {
                        removeSupplier(removeSupplier: {id: "${supplier.id}"}) {
                            data { id }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.suppliers.removeSupplier.data;
                }),
            );
    }

    public uploadFile(fileToUpload: File, supplierId: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('supplierId', supplierId);

        return this.http.post(uploadURL, formData, { responseType: 'text' });
    }

    public deleteSupplierLogo(supplierId: string): Observable<any> {
        return this.http.delete(deleteURL.concat('?fileType=logo&supplierId=', supplierId));
    }

    public deleteSupplierTermsAndConditions(supplierId: string): Observable<any> {
        return this.http.delete(deleteURL.concat('?fileType=termsAndConditions&supplierId=', supplierId));
    }

    public getSupplierTokenInfo(ids: string[]): Observable<SupplierTokenLimit[]> {
        return this.http.post<SupplierTokenLimit[]>(
            environment.BASE_URL + 'supplierManagementApi/suppliers/get-suppliers-token-limit-information',
            { ids },
        );
    }

    public getSupplierTokenLimit(supplierId: string): Observable<SupplierTokenLimitResponse> {
        return this.http.get<SupplierTokenLimitResponse>(
            `${environment.BASE_URL}supplierManagementApi/suppliers/${supplierId}/token-limit`,
            {
                // If no limit was ever configured, the supplier AI settings are considered "Inactive" and BE returns 404
                // So we will treat this as 0, without displaying error modal
                context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [404]),
            },
        );
    }

    public saveSupplierTokenLimit(supplierId: string, body: { tokenLimit: number; isActive: boolean }, isUpdate: boolean): Observable<any> {
        const url = `${environment.BASE_URL}supplierManagementApi/suppliers/${supplierId}/token-limit`;
        return isUpdate ? this.http.put(url, body) : this.http.post(url, body);
    }

    public getSupplierSpendingLimits(supplierId: string): Observable<SupplierSpendingLimit[]> {
        // A supplier with no limit answers with an empty array, so the error handler stays on.
        // REST puts the usage on the wire as a number and GraphQL as a name, so map it here
        // and let every caller read one type whichever of the two reads it used.
        return this.http.get<SupplierSpendingLimitResponse[]>(this.getSpendingLimitsURL(supplierId)).pipe(
            map((limits) =>
                limits.map((limit) => ({
                    usage: getAiUsageTypeFromCode(limit.usage),
                    limitAmount: limit.limitAmount,
                    isUnlimited: limit.isUnlimited,
                    markupPercentage: limit.markupPercentage,
                    isActive: limit.isActive,
                })),
            ),
        );
    }

    public saveSupplierSpendingLimit(supplierId: string, usage: AiUsageType, body: SetSupplierSpendingLimitRequest): Observable<any> {
        // The route names the row, so one PUT both creates and updates it.
        return this.http.put(this.getSpendingLimitForUsageURL(supplierId, usage), body);
    }

    public removeSupplierSpendingLimit(supplierId: string, usage: AiUsageType): Observable<any> {
        return this.http.delete(this.getSpendingLimitForUsageURL(supplierId, usage));
    }

    private getSpendingLimitsURL(supplierId: string): string {
        return `${supplierManagementURL}suppliers/${supplierId}/spending-limits`;
    }

    private getSpendingLimitForUsageURL(supplierId: string, usage: AiUsageType): string {
        return `${this.getSpendingLimitsURL(supplierId)}/${getAiUsageTypeRoute(usage)}`;
    }
}
